import { z } from 'zod';
/**
 * Múi giờ của tổ chức — APM-TZ-a/b.
 *
 * ## Vì sao cần
 *
 * Lịch làm việc lưu giờ trong ngày (`08:00–17:00`) mà không kèm múi giờ, và
 * trước APM-TZ server hiểu chúng là **UTC**. Với một tổ chức ở +07:00 thì ca
 * sáng rơi vào 15:00–24:00 giờ địa phương: nút bấm giờ báo "ngoài giờ" cả buổi
 * sáng, và job tự tạm dừng đóng đoạn vào nửa đêm.
 *
 * ## Tên IANA, không phải độ lệch
 *
 * `+07:00` không diễn đạt được giờ mùa hè. Một tổ chức ở Berlin khai `+01:00`
 * sẽ lệch một tiếng suốt nửa năm, và lệch im lặng — ca vẫn mở, chỉ là sai giờ.
 *
 * ## Vì sao nằm ở contracts
 *
 * Phép quy đổi dùng ở cả hai bên: server dựng ca tuyệt đối, còn ô chọn múi giờ
 * ở giao diện xem trước "bây giờ ở đó là mấy giờ". Hai bản cài đặt của cùng một
 * phép tính giờ sẽ lệch nhau đúng vào ngày đổi giờ.
 *
 * Không thêm thư viện: `Intl.DateTimeFormat` có sẵn ở Node 22 và mọi trình
 * duyệt đang hỗ trợ, và nó mang theo cơ sở dữ liệu múi giờ của chính runtime.
 */
/** Mặc định của mọi tổ chức — khớp `DEFAULT` của cột `organization.timezone`. */
export const DEFAULT_ORG_TIMEZONE = 'Asia/Ho_Chi_Minh';
/**
 * Runtime có nhận tên múi giờ này không.
 *
 * ⚠️ Không dùng `Intl.supportedValuesOf('timeZone')` làm cửa: danh sách ấy chỉ
 * gồm tên **chuẩn tắc**, nên nó từ chối `UTC` và các bí danh hợp lệ như
 * `Asia/Saigon` — trong khi `DateTimeFormat` vẫn dùng được chúng.
 *
 * ⚠️ Chặn **tường minh** độ lệch dạng `+07:00`: từ ES2024 `DateTimeFormat` nhận
 * cả nó làm `timeZone`, nên chỉ thử-rồi-bắt-lỗi là để lọt đúng thứ mà cột này
 * tồn tại để tránh — một độ lệch cố định không biết giờ mùa hè.
 */
export function isValidTimezone(value) {
    if (/^[+-]/.test(value))
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
export const IanaTimezone = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(isValidTimezone, 'Múi giờ không hợp lệ');
export const OrgTimezoneView = z.object({ timezone: IanaTimezone });
export const UpdateOrgTimezoneInput = OrgTimezoneView;
/** Một `DateTimeFormat` cho mỗi múi giờ — dựng nó tốn hơn dùng nó nhiều lần. */
const formatters = new Map();
function formatterFor(timezone) {
    let formatter = formatters.get(timezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatters.set(timezone, formatter);
    }
    return formatter;
}
/** Ngày giờ "trên đồng hồ treo tường" ở múi giờ ấy tại mốc `at`. */
function wallClock(at, timezone) {
    const parts = {};
    for (const part of formatterFor(timezone).formatToParts(at)) {
        if (part.type !== 'literal')
            parts[part.type] = Number(part.value);
    }
    return parts;
}
/**
 * Độ lệch so với UTC, tính bằng phút, tại mốc `at` — `+420` cho Việt Nam.
 *
 * Phải hỏi **tại một mốc**, không hỏi một lần cho cả múi giờ: độ lệch của
 * Berlin là +60 vào tháng Một và +120 vào tháng Bảy.
 */
export function timezoneOffsetMinutes(at, timezone) {
    const p = wallClock(at, timezone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    // Bỏ phần mili giây của `at`: `formatToParts` không trả nó, và để lại thì
    // độ lệch ra số lẻ.
    return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60_000);
}
/** Phút kể từ nửa đêm **địa phương** tại mốc `at` — `0…1439`. */
export function minutesOfDayIn(at, timezone) {
    const p = wallClock(at, timezone);
    return p.hour * 60 + p.minute;
}
/** Ngày dương lịch ở múi giờ ấy chứa mốc `at` — `YYYY-MM-DD`. */
export function calendarDateIn(at, timezone) {
    const p = wallClock(at, timezone);
    return `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
/**
 * "Hôm nay" của múi giờ ấy, dưới dạng **nửa đêm UTC của ngày địa phương** —
 * APM-TZ-l.
 *
 * Dành cho các hàm thuần đọc ngày bằng `at.toISOString().slice(0, 10)` hoặc
 * `getUTC*` (luật kỳ OKR, `goalPeriod`, `evaluateGoal`): chúng coi `at` là một
 * ngày lịch, và mốc neo này làm cho phép đọc UTC ấy ra đúng ngày của tổ chức.
 * Đừng dùng nó làm một mốc thời gian — nó lệch khỏi "bây giờ" tới 14 tiếng.
 */
export function localDayAnchor(at, timezone) {
    return new Date(`${calendarDateIn(at, timezone)}T00:00:00.000Z`);
}
/**
 * Mốc tuyệt đối của "`minuteOfDay` phút sau nửa đêm ngày `date`" theo giờ địa
 * phương của `timezone`. `minuteOfDay = 1440` cho ra nửa đêm của ngày hôm sau.
 *
 * ## Thử độ lệch của hôm trước và hôm sau, không đoán một lần
 *
 * Độ lệch phụ thuộc chính mốc đang tìm, nên không lấy thẳng được. Nhưng mọi
 * múi giờ đổi giờ nhiều nhất một lần trong một ngày, nên độ lệch đúng luôn là
 * độ lệch của 24 giờ trước hoặc 24 giờ sau. Ứng viên nào quy ngược về đúng giờ
 * địa phương thì hợp lệ.
 *
 * Ngày đổi giờ theo cách mà `Temporal` gọi là `disambiguation: 'compatible'`:
 * giờ **lặp hai lần** (02:30 ở Berlin sáng Chủ nhật cuối tháng Mười) lấy lần
 * sớm hơn, và giờ **không tồn tại** (02:30 cuối tháng Ba) đẩy về sau đúng
 * khoảng nhảy.
 */
export function zonedTimeToUtc(date, minuteOfDay, timezone) {
    const [year, month, day] = date.split('-').map(Number);
    const naive = Date.UTC(year, month - 1, day) + minuteOfDay * 60_000;
    const DAY = 86_400_000;
    const before = timezoneOffsetMinutes(new Date(naive - DAY), timezone);
    const after = timezoneOffsetMinutes(new Date(naive + DAY), timezone);
    const valid = [naive - before * 60_000, naive - after * 60_000].filter((candidate) => candidate + timezoneOffsetMinutes(new Date(candidate), timezone) * 60_000 === naive);
    if (valid.length > 0)
        return new Date(Math.min(...valid));
    // Giờ bị nhảy qua: dùng độ lệch **trước** khi đổi ⇒ kết quả rơi sau khoảng nhảy.
    return new Date(naive - before * 60_000);
}
//# sourceMappingURL=timezone.js.map